/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, April 18th 2023, 6:14:26 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useSubscriptionContext } from "saas-subscription/providers";
import { getUnitAmount } from "saas-subscription/utils";
import { DebitCardIcon } from "theme/icons";
import { EmphasisText } from "ui-widgets";
import { usePurchaseFlowStrategy } from "./usePurchaseFlowStrategy";
import { useSubscribeWithPay } from "./useSubscribeWithPay";

export const usePayUpfrontPurchaseFlowStrategy: typeof usePurchaseFlowStrategy = () => {
  const parentStrategy = usePurchaseFlowStrategy();
  const { subscriptionTracker } = useSubscriptionContext();

  return {
    ...parentStrategy,
    getPurchaseFormTitle: (subscriptionPlan) => `Buy ${subscriptionPlan.name}`,
    getPurchaseFormIcon: (plan) => <DebitCardIcon height={50} width={50} />,
    getPurchaseFormBody: (args) => (
      <PayUpfrontFormBody
        packageColor={parentStrategy.getWidgets(args.subscriptionPlan).packageColor}
        selectedPrice={args.selectedPrice}
        subscriptionPlan={args.subscriptionPlan}
        selectedOrganization={args.selectedOrganization}
      />
    ),
    getPurchaseCTA: ({ onClickPlan, subscriptionPlan }) => {
      const packageWidgets = parentStrategy.getWidgets(subscriptionPlan);
      return (
        <Button
          onClick={onClickPlan}
          fullWidth
          size="large"
          variant="contained"
          sx={{ backgroundColor: packageWidgets.packageColor }}
        >
          {subscriptionTracker?.product_code ? <>{packageWidgets.upgradeText}</> : <>Buy {packageWidgets.shortName}</>}
        </Button>
      );
    },
    useHandlePurchase: ({ selectedOrganization }) => {
      return { ...useSubscribeWithPay(selectedOrganization) };
    },
  };
};

const PayUpfrontFormBody = ({
  packageColor,
  selectedPrice,
  subscriptionPlan,
  selectedOrganization,
}: Parameters<ReturnType<typeof usePayUpfrontPurchaseFlowStrategy>["getPurchaseFormBody"]>[0]) => (
  <Stack spacing={2}>
    <Typography variant="body1" color="gray">
      You have chosen to purchase the <EmphasisText color={packageColor}>{subscriptionPlan.name}</EmphasisText> plan for
      your organization named <EmphasisText>{selectedOrganization?.name}</EmphasisText>.
    </Typography>
    <Typography variant="body1" color="gray">
      Please accept our terms of service and proceed to make payment. You will be billed{" "}
      <EmphasisText color={packageColor}>
        {getUnitAmount({ price: selectedPrice, withCurrency: true })} per {subscriptionPlan.unit_label} per{" "}
        {selectedPrice.recurring.interval}
      </EmphasisText>{" "}
      until you cancel your subscription or downgrade to a free plan.
    </Typography>
  </Stack>
);
